import { useMemo } from 'react'
import {
  GridOptions,
  SuppressKeyboardEventParams,
  ValueSetterParams,
} from 'ag-grid-community'
import {
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { intl } from '../../../../i18n'
import { ProgramManagementRow } from '../programManagement'
import { programItemRowService } from '../programItemRowService'
import { ProgramItemTreeCellRenderer } from '../components/ProgramItemTreeCellRenderer'
import { useColumnDefs } from './columnDefs'

// 選択行の枠線（pm-row-selected-* クラス付与と onSelectionChanged 連携）は BulkSheetView 側に
// 共通実装し、ProgramManagement では rootClassName と selectionRowFrameEnabled で有効化する。

export const useProgramManagementGridOptions = (
  // プロジェクト行アイコンのクリックハンドラ（権限・フラグ判定済み。無効時は undefined）。
  onProjectIconClick?: (row: ProgramManagementRow) => void
): GridOptions<ProgramManagementRow> => {
  const columnDefs = useColumnDefs()

  const gridOptions: GridOptions<ProgramManagementRow> = useMemo(
    () => ({
      // ヘッダー・行の高さ
      groupHeaderHeight: 25,
      headerHeight: 45,
      rowHeight: 32,

      // コンテキスト（ページコンポーネントから上書きされる）
      context: {},

      suppressLastEmptyLineOnPaste: true,

      // 行操作
      rowDragManaged: false,
      rowDragMultiRow: true,
      suppressMoveWhenRowDragging: true,
      enterNavigatesVerticallyAfterEdit: true,

      // カラム型・コンポーネント
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColGroupDef: { suppressHeaderContextMenu: true },
      defaultColDef: {
        cellDataType: false,
        width: 85,
        editable: false,
        enableValue: false,
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        suppressSizeToFit: true,
        singleClickEdit: false,
        cellStyle: { justifyContent: 'left' },
        cellRenderer: DefaultCellRenderer,
        onCellClicked: defaultOnCellClicked,
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          // 日本語入力中はキーボードイベントを抑制する
          if (params.editing && params.event.isComposing) {
            return true
          }
          return (
            !params.editing &&
            ['Delete', 'Backspace'].includes(params.event.key)
          )
        },
      },
      columnDefs,

      // プログラム・グループ・プロジェクト行は太字にする
      // （ProjectPlanNew の成果物以上を太字にするのと同じスタイル）
      getRowStyle: params => {
        const data = params.data
        if (
          data &&
          (programItemRowService.isProgramRow(data) ||
            programItemRowService.isGroupRow(data) ||
            programItemRowService.isProjectRow(data))
        ) {
          return { fontWeight: 'bold' }
        }
        return undefined
      },

      // ツリーデータ
      treeData: true,
      excludeChildrenWhenTreeDataFiltering: true,
      groupDefaultExpanded: -1,
      autoGroupColumnDef: {
        headerName: intl.formatMessage({
          id: 'programManagement.column.name',
        }),
        field: 'displayName',
        editable: params =>
          !!params.data && programItemRowService.isGroupRow(params.data),
        // 編集可能なグループ行では Delete/Backspace による値クリア・編集開始を許可する
        // （defaultColDef は非編集時の Delete/Backspace を一律抑止しているため上書き）
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          if (params.editing && params.event.isComposing) return true
          if (
            !params.editing &&
            ['Delete', 'Backspace'].includes(params.event.key)
          ) {
            return !(
              params.data && programItemRowService.isGroupRow(params.data)
            )
          }
          return false
        },
        valueSetter: (
          params: ValueSetterParams<ProgramManagementRow>
        ): boolean => {
          if (!params.data) return false
          // レンジ削除（Delete）では null が渡るため空文字に正規化する
          params.data.displayName = (params.newValue as string) ?? ''
          params.data.edited = true
          return true
        },
        cellClassRules: {
          'hover-over-can-drop': params => {
            return (
              params.context?.onTree &&
              params.context?.draggableNodeId &&
              params.node?.id === params.context.draggableNodeId
            )
          },
        },
        cellRendererParams: {
          suppressCount: true,
          // ダブルクリック編集時にツリーが開閉しないようにする
          // （ツリーの開閉は開閉アイコンのクリック時のみ）
          suppressDoubleClickExpand: true,
          innerRenderer: ProgramItemTreeCellRenderer,
          // プロジェクト行アイコンのクリックは context ではなくセル固有の引数で渡す。
          onProjectIconClick,
          validate: (
            value: string,
            params: { data: ProgramManagementRow }
          ): string | undefined => {
            if (
              !programItemRowService.isGroupRow(params.data) ||
              !params.data.edited
            ) {
              return undefined
            }
            if (!value || value.trim() === '') {
              return intl.formatMessage({
                id: 'programManagement.validation.groupNameRequired',
              })
            }
            return undefined
          },
        },
        width: 300,
        pinned: 'left' as const,
      },
      processUnpinnedColumns: () => [],
    }),
    [columnDefs, onProjectIconClick]
  )

  return gridOptions
}
